import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { RegistryOrderError } from '@/services/registry-order-error';
import { vnextApiClient } from '@/services/vnext-api';
import {
  newerRegistryDigestStatus,
  type RegistryDigestAddedToReport,
  type RegistryDigestAddToReportPreview,
  type RegistryDigestStatus,
  type RegistryDocumentDigest,
  type RegistryOrderDetail,
} from '@/types/registry-documents';
import { saveUrlAs } from '@/utils/downloads';
import { toError } from '@/utils/errors';
import { usePoll } from './usePoll';

/** While the documents are being read the order is re-read every few
 * seconds, so the tables appear as the person watches. */
export const REGISTRY_DIGEST_POLL_MS = 3_000;

/** How long the quick cadence lasts. The API gives a run forty minutes
 * (it reads every page of every delivered document) and marks one still
 * running after forty-five as failed, so nothing is learnt by polling past
 * that: the person is told to check again instead. */
export const REGISTRY_DIGEST_POLL_MAX_MS = 45 * 60_000;

/** What this hook learnt about one order's run, kept apart from the row's
 * reading so a change of order never shows another order's state. */
interface LearntStatus {
  orderId: string;
  status: RegistryDigestStatus;
}

/** The digest read for one completed run, keyed so a re-run's tables
 * replace the last run's instead of showing beside its status. */
interface DigestRead {
  key: string;
  digest: RegistryDocumentDigest | null;
}

/** The markdown read for one completed run, keyed the same way. */
interface MarkdownRead {
  key: string;
  markdown: string;
}

/**
 * The digest as report markdown for the completed run `completedKey`
 * names, read on request (the clipboard's) and kept until a later run
 * completes, when it is read afresh. The ref answers a second call
 * before React has re-rendered with the state, so one run is read once
 * however quickly the button is pressed twice.
 */
function useDigestMarkdown(
  orderId: string,
  completedKey: string | null
): Pick<UseRegistryDigestResult, 'markdown' | 'isLoadingMarkdown' | 'loadMarkdown'> {
  const [isLoadingMarkdown, setLoadingMarkdown] = useState(false);
  const [markdownRead, setMarkdownRead] = useState<MarkdownRead | null>(null);
  const markdownRef = useRef<MarkdownRead | null>(null);

  const loadMarkdown = useCallback(async () => {
    if (completedKey === null) {
      throw new Error('There is no completed extraction to copy');
    }
    const cached = markdownRef.current;
    if (cached && cached.key === completedKey) {
      return cached.markdown;
    }
    setLoadingMarkdown(true);
    try {
      const { markdown } = await vnextApiClient.getRegistryDigestMarkdown(orderId);
      const read = { key: completedKey, markdown };
      markdownRef.current = read;
      setMarkdownRead(read);
      return markdown;
    } catch (err) {
      // The caller says how it went, beside the button it followed; a
      // failure that is no refusal is reported as the others are.
      if (!(err instanceof RegistryOrderError)) {
        Sentry.captureException(toError(err));
      }
      throw toError(err);
    } finally {
      setLoadingMarkdown(false);
    }
  }, [orderId, completedKey]);

  return {
    markdown: markdownRead?.key === completedKey ? markdownRead.markdown : null,
    isLoadingMarkdown,
    loadMarkdown,
  };
}

export interface UseRegistryDigestResult {
  /** The extraction's state as best known: the row's reading, overtaken by
   * what starting a run and polling learnt since. */
  status: RegistryDigestStatus | null;
  /** The tables, once the run is complete and they have been read. */
  digest: RegistryDocumentDigest | null;
  /** The digest as report markdown for the run shown, once read for the
   * clipboard; null until then, and again for a later run. */
  markdown: string | null;
  isStarting: boolean;
  isLoadingDigest: boolean;
  isDownloading: boolean;
  /** The markdown is being read for the clipboard. */
  isLoadingMarkdown: boolean;
  /** An insertion into the report is in flight. */
  isAddingToReport: boolean;
  /** Polling ran out of time with the run still marked running. */
  isStalled: boolean;
  /** Why the last start, read, download, preview or insertion failed;
   * cleared by the next. */
  error: Error | null;
  /** Start a run (or another one) and follow it. */
  start: () => Promise<void>;
  /** Save the Word file under the API's filename. */
  downloadDocx: () => Promise<void>;
  /** The digest as report markdown, read once per completed run and kept
   * (`markdown`); throws when it cannot be read, or no run is complete. */
  loadMarkdown: () => Promise<string>;
  /** Re-read the order's state and, when complete, the tables. */
  refresh: () => Promise<void>;
  /** Append the digest to the report as a new version, built from
   * `expectedContentVersion` (the dry run's). Null when refused: `error`
   * says why. */
  addToReport: (
    expectedContentVersion?: number | null
  ) => Promise<RegistryDigestAddedToReport | null>;
  /** Ask what the insertion would do without writing. Null when refused:
   * `error` says why. */
  previewAddToReport: () => Promise<RegistryDigestAddToReportPreview | null>;
}

/**
 * The extraction of information from one order's delivered documents,
 * from the button to the tables.
 *
 * The state comes in on the order row (`status`) and is overtaken by what
 * this hook learns: the 202 that starting a run answers, then the order's
 * detail polled every few seconds while the run is `running`, until it is
 * `completed` or `failed` or the polling window closes. Once a run is
 * complete the digest is read once per run. A run is told apart by when
 * it was asked for, so "Run again" restarts both the polling clock and
 * the read.
 */
export function useRegistryDigest(
  orderId: string,
  status: RegistryDigestStatus | null | undefined
): UseRegistryDigestResult {
  const [learnt, setLearnt] = useState<LearntStatus | null>(null);
  const [isStarting, setStarting] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const [isAddingToReport, setAddingToReport] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  // The run whose polling window closed, so a later run starts unstalled.
  const [stalledRun, setStalledRun] = useState<string | null>(null);

  const own = learnt?.orderId === orderId ? learnt.status : null;
  const effective = newerRegistryDigestStatus(own, status);
  const running = effective?.status === 'running';
  const runKey = effective ? `${orderId}:${effective.requestedAt ?? ''}` : null;
  const runKeyRef = useRef(runKey);
  runKeyRef.current = runKey;

  const learn = useCallback(
    (next: RegistryDigestStatus | null | undefined) => {
      if (next) {
        setLearnt({ orderId, status: next });
      }
    },
    [orderId]
  );

  // While a run is reading, the order's detail says when it is done.
  const watch = usePoll<RegistryOrderDetail>({
    interval: REGISTRY_DIGEST_POLL_MS,
    enabled: running,
    // Right after a start the run cannot have finished: the first read
    // waits one interval rather than asking at once.
    immediate: false,
    maxDurationMs: REGISTRY_DIGEST_POLL_MAX_MS,
    refetchDeps: [runKey],
    queryFn: () => vnextApiClient.getRegistryOrder(orderId),
    onData: (detail) => learn(detail.digest),
    onStop: (reason) => {
      if (reason === 'maxDuration') {
        setStalledRun(runKeyRef.current);
      }
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  // Once complete, the tables are read once for that run.
  const completedKey =
    effective?.status === 'completed' ? `${runKey}:${effective.finishedAt ?? ''}` : null;
  const read = usePoll<DigestRead>({
    interval: null,
    enabled: completedKey !== null,
    refetchDeps: [completedKey],
    queryFn: async () => {
      const result = await vnextApiClient.getRegistryDigest(orderId);
      // The read carries the state too, and it is the freshest reading.
      learn(result.status);
      return { key: completedKey as string, digest: result.digest };
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const digest = read.data && read.data.key === completedKey ? read.data.digest : null;
  const isLoadingDigest =
    completedKey !== null && read.data?.key !== completedKey && read.error === undefined;
  const { markdown, isLoadingMarkdown, loadMarkdown } = useDigestMarkdown(orderId, completedKey);

  // A refusal the API typed is the person's to read; anything else (a
  // dropped connection, a bug) is reported too.
  const fail = useCallback((err: unknown) => {
    if (!(err instanceof RegistryOrderError)) {
      Sentry.captureException(toError(err));
    }
    setActionError(toError(err));
  }, []);

  const start = useCallback(async () => {
    setStarting(true);
    setActionError(null);
    try {
      learn(await vnextApiClient.startRegistryDigest(orderId));
    } catch (err) {
      if (err instanceof RegistryOrderError && err.kind === 'digest_in_progress') {
        // Someone else is already reading the documents: show that run
        // rather than the refusal, when the order can be re-read and
        // carries a run to show. Otherwise the refusal stands, so the
        // click never ends in silence.
        try {
          const run = (await vnextApiClient.getRegistryOrder(orderId)).digest;
          if (run) {
            learn(run);
            return;
          }
        } catch (readErr) {
          Sentry.captureException(toError(readErr));
        }
      }
      fail(err);
    } finally {
      setStarting(false);
    }
  }, [orderId, learn, fail]);

  const downloadDocx = useCallback(async () => {
    setDownloading(true);
    setActionError(null);
    try {
      const { url, filename } = await vnextApiClient.getRegistryDigestDocx(orderId);
      // Fetched and saved under the API's name, as the other presigned
      // downloads are: a `window.open` after an await is outside the
      // click's gesture and Safari's pop-up blocker refuses it silently.
      await saveUrlAs(url, filename);
    } catch (err) {
      fail(err);
    } finally {
      setDownloading(false);
    }
  }, [orderId, fail]);

  const addToReport = useCallback(
    async (expectedContentVersion?: number | null) => {
      setAddingToReport(true);
      setActionError(null);
      try {
        const result = await vnextApiClient.addRegistryDigestToReport(orderId, {
          expectedContentVersion,
        });
        // The answer carries the state with the insertion on it: the
        // "Added to report" line reads from it without a re-read.
        learn(result.digest);
        return result;
      } catch (err) {
        fail(err);
        return null;
      } finally {
        setAddingToReport(false);
      }
    },
    [orderId, learn, fail]
  );

  const previewAddToReport = useCallback(async () => {
    setActionError(null);
    try {
      return await vnextApiClient.previewRegistryDigestAddToReport(orderId);
    } catch (err) {
      fail(err);
      return null;
    }
  }, [orderId, fail]);

  const { refetch: refetchWatch } = watch;
  const { refetch: refetchRead } = read;
  const refresh = useCallback(async () => {
    setActionError(null);
    await (completedKey !== null ? refetchRead() : refetchWatch());
  }, [completedKey, refetchRead, refetchWatch]);

  const readError = read.error === undefined ? null : toError(read.error);

  return {
    status: effective,
    digest,
    markdown,
    isStarting,
    isLoadingDigest,
    isDownloading,
    isLoadingMarkdown,
    isAddingToReport,
    isStalled: running && stalledRun === runKey,
    error: actionError ?? readError,
    start,
    downloadDocx,
    loadMarkdown,
    refresh,
    addToReport,
    previewAddToReport,
  };
}
